import { Menu, Bell, Wifi, WifiOff, Zap, RefreshCw, HelpCircle } from 'lucide-react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useUIStore } from '../../store/uiStore'
import { useAuthStore } from '../../store/authStore'
import { supabase } from '../../lib/supabase'
import { useOnlineStatus } from '../../hooks/useOnlineStatus'
import { syncPendingOps } from '../../lib/syncManager'
import { cn } from '../../utils/cn'
import toast from 'react-hot-toast'
import { TrialCountdownBadge, useAccessLapsed } from '../ui/TrialCountdownBadge'
import { OnlineStorePill } from '../online-store/OnlineStorePill'
import { CustomDomainChip } from '../online-store/CustomDomainChip'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = (t: string): any => (supabase as any).from(t)

export function TopBar() {
  const { toggleSidebar } = useUIStore()
  const { tenant } = useAuthStore()
  const tid = tenant?.id ?? ''
  const { isOnline, pendingCount } = useOnlineStatus()
  const online = isOnline
  const navigate = useNavigate()
  const { lapsed, isTrial } = useAccessLapsed()

  const { data: palstroUnread } = useQuery<{ count: number; topPriority: string }>({
    queryKey: ['palstro-unread', tid],
    enabled: !!tid,
    refetchInterval: 60 * 1000,
    queryFn: async () => {
      const { data } = await db('palstro_messages')
        .select('priority')
        .eq('tenant_id', tid)
        .eq('is_read', false)
      const msgs = (data ?? []) as Array<{ priority: string }>
      const count = msgs.length
      const hasCritical = msgs.some(m => m.priority === 'critical')
      const hasImportant = msgs.some(m => m.priority === 'important')
      const topPriority = hasCritical ? 'critical' : hasImportant ? 'important' : 'normal'
      return { count, topPriority }
    },
  })

  const palstroCount = palstroUnread?.count ?? 0
  const palstroPriority = palstroUnread?.topPriority ?? 'normal'
  const palstroDotClass = palstroPriority === 'critical'
    ? 'bg-red-500 animate-palstro-pulse-critical'
    : palstroPriority === 'important'
    ? 'bg-amber-500 animate-palstro-pulse-important'
    : 'bg-brand-500 animate-palstro-pulse'

  const { data: unreadCount = 0 } = useQuery<number>({
    queryKey: ['notification-count', tid],
    enabled: !!tid,
    queryFn: async () => {
      const { count } = await db('notifications')
        .select('id', { count: 'exact', head: true })
        .eq('tenant_id', tid)
        .eq('is_read', false)
      return count ?? 0
    },
    staleTime: 1000 * 30, // 30 seconds for notifications
    refetchInterval: 1000 * 60, // poll every minute
  })

  return (
    <header className="sticky top-0 z-20 border-b border-gray-100 bg-white">
      {/* Offline banner */}
      {!online && (
        <div className="bg-amber-500 text-white text-xs text-center py-1.5 flex items-center justify-center gap-1.5">
          <WifiOff className="h-3.5 w-3.5" />
          Offline — reconnect to save your work. Unsaved changes may be lost.
        </div>
      )}

      {/* Lapsed-access banner — only when the billing period end is genuinely in the past */}
      {lapsed && (
        <div className="bg-red-500 text-white text-xs text-center py-1.5 flex items-center justify-center gap-2">
          <Zap className="h-3.5 w-3.5" />
          {isTrial ? 'Your free trial has ended.' : 'Your subscription has expired.'}{' '}
          <Link to="/subscription" className="font-bold underline">
            {isTrial ? 'Subscribe now' : 'Renew now'}
          </Link>{' '}
          to continue using Palstro.
        </div>
      )}

      <div className="flex h-14 items-center justify-between gap-3 px-4">
        <div className="flex items-center gap-2 min-w-0">
          <button
            onClick={toggleSidebar}
            className="lg:hidden flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl text-gray-500 hover:bg-gray-100 transition-colors"
            aria-label="Open menu"
          >
            <Menu className="h-5 w-5" />
          </button>
          <span className="text-sm font-semibold text-gray-800 lg:hidden min-w-0 truncate">{tenant?.name ?? 'Palstro'}</span>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Connection status + pending sync */}
          {online ? (
            pendingCount > 0 ? (
              <button
                onClick={async () => {
                  const result = await syncPendingOps()
                  toast.success(`Synced ${result.synced} record(s)`)
                }}
                className="hidden sm:flex items-center gap-1.5 text-xs text-brand-600 bg-brand-50 px-2 py-1 rounded-lg hover:bg-brand-100"
              >
                <RefreshCw className="h-3.5 w-3.5" /> Sync {pendingCount}
              </button>
            ) : (
              <span className="hidden sm:flex items-center gap-1 text-xs text-sage-600">
                <Wifi className="h-3.5 w-3.5" />
              </span>
            )
          ) : (
            <span className="hidden sm:flex items-center gap-1 text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded-lg">
              <WifiOff className="h-3.5 w-3.5" /> Offline
            </span>
          )}

          {/* Subscription pill */}
          <TrialCountdownBadge />

          {/* Online-store add-on pill + custom-domain status chip (owner-only) */}
          <CustomDomainChip />
          <OnlineStorePill />

          {/* Palstro messages indicator */}
          <button
            onClick={() => navigate('/palstro-messages')}
            className="relative flex h-11 w-11 items-center justify-center rounded-xl hover:bg-orange-50 transition-colors"
            title="Messages from Palstro"
          >
            <div className="h-6 w-6 rounded-md bg-brand-500 flex items-center justify-center">
              <span className="text-white text-xs font-black">P</span>
            </div>
            {palstroCount > 0 && (
              <span className={cn('absolute -top-0.5 -right-0.5 h-3 w-3 rounded-full', palstroDotClass)} />
            )}
          </button>

          {/* Help guide */}
          <Link
            to="/help"
            className="relative flex h-11 w-11 items-center justify-center rounded-xl text-gray-500 hover:bg-gray-100 transition-colors"
            aria-label="Help"
            title="Help guide"
          >
            <HelpCircle className="h-5 w-5" />
          </Link>

          {/* Notification bell */}
          <Link
            to="/notifications"
            className="relative flex h-11 w-11 items-center justify-center rounded-xl text-gray-500 hover:bg-gray-100 transition-colors"
            aria-label="Notifications"
          >
            <Bell className="h-5 w-5" />
            {unreadCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 h-4 w-4 rounded-full bg-brand-500 text-white text-[9px] font-bold flex items-center justify-center ring-2 ring-white">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </Link>
        </div>
      </div>
    </header>
  )
}
